import React from 'react';
import LoadingSpinner from './LoadingSpinner';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  fullWidth?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = 'primary',
      size = 'md',
      loading = false,
      icon,
      iconPosition = 'left',
      fullWidth = false,
      className = '',
      disabled,
      ...props
    },
    ref
  ) => {
    const baseClasses =
      'inline-flex items-center justify-center font-medium rounded-token-md transition-all duration-token ease-token focus:outline-none focus:ring-2 focus:ring-token-focus focus:ring-offset-token-focus disabled:opacity-50 disabled:cursor-not-allowed';

    const variantClasses = {
      primary: 'bg-token-primary hover:bg-token-primary-hover text-white',
      secondary: 'bg-token-secondary hover:bg-token-secondary-hover text-white',
      success: 'bg-token-success hover:bg-token-success-hover text-white',
      danger: 'bg-token-danger hover:bg-token-danger-hover text-white',
      warning: 'bg-token-warning hover:bg-token-warning-hover text-white',
      info: 'bg-token-primary hover:bg-token-primary-hover text-white',
    };

    const sizeClasses = {
      sm: 'px-token-sm py-token-xs text-sm',
      md: 'px-token-md py-token-sm text-base',
      lg: 'px-token-lg py-token-md text-lg',
    };

    const iconSizeClasses = {
      sm: 'h-4 w-4',
      md: 'h-5 w-5',
      lg: 'h-6 w-6',
    };

    const widthClass = fullWidth ? 'w-full' : '';

    const isDisabled = disabled || loading;

    return (
      <button
        ref={ref}
        className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${widthClass} ${className}`}
        disabled={isDisabled}
        {...props}
      >
        {loading && (
          <LoadingSpinner size={size === 'lg' ? 'md' : 'sm'} color="gray" className="mr-token-sm" />
        )}

        {!loading && icon && iconPosition === 'left' && (
          <span
            className={`flex shrink-0 items-center justify-center ${iconSizeClasses[size]} ${children ? 'mr-token-sm' : ''}`}
          >
            {icon}
          </span>
        )}

        {children}

        {!loading && icon && iconPosition === 'right' && (
          <span
            className={`flex shrink-0 items-center justify-center ${iconSizeClasses[size]} ${children ? 'ml-token-sm' : ''}`}
          >
            {icon}
          </span>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
